import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { ApiResponse } from '@demoops/shared-types';

/**
 * Performing an allowlisted action (Phase 3).
 *
 * The client sends an action NAME and the id it applies to — never an
 * endpoint, a method or a payload. What that name maps to lives server-side in
 * GuideActionRegistry, so the browser cannot describe a call, only pick one
 * from a fixed list. The server re-checks the permission and the confirmation
 * regardless of what the UI did.
 */

export interface GuideActPayload {
  action: string;
  entity_id: string;
  /** Sent only after the user has confirmed; the server refuses without it. */
  confirm: boolean;
}

export interface GuideActResult {
  entity_id: string;
  status: string;
  submitted_at?: string | null;
}

export function useGuideAct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: GuideActPayload) =>
      (await api.post<ApiResponse<GuideActResult>>('/api/v1/guide/act', payload)).data,
    // Everything, deliberately. A state transition ripples further than its
    // own record — the log, the project's log list, the dashboard's missing-log
    // count — and actions are rare, confirmed and few. A stale page still
    // reading "draft" after a Guide-driven submit is worse than a refetch.
    onSuccess: () => { qc.invalidateQueries(); },
  });
}
